import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, User, Bot, Maximize2, Minimize2, GripHorizontal } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../styles/Chatbot.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Chatbot = ({ position = 'bottom-right' }) => {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackData, setCallbackData] = useState({ name: '', email: '', question: '' });
  const messagesEndRef = useRef(null);
  
  // Drag functionality
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [windowPosition, setWindowPosition] = useState({ x: null, y: null });
  const chatWindowRef = useRef(null);

  // Resize functionality
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [windowSize, setWindowSize] = useState({ width: null, height: null });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // Handle resize start
  const handleResizeStart = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    if (chatWindowRef.current) {
      const rect = chatWindowRef.current.getBoundingClientRect();
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height
      });
      setResizeDirection(direction);
      setIsResizing(true);
    }
  }, []);

  // Handle resizing
  const handleResize = useCallback((e) => {
    if (!isResizing || !resizeDirection) return;

    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;

    // Calculate new size based on direction
    if (resizeDirection.includes('e')) {
      newWidth = Math.max(320, Math.min(resizeStart.width + deltaX, window.innerWidth - 40));
    }
    if (resizeDirection.includes('w')) {
      newWidth = Math.max(320, Math.min(resizeStart.width - deltaX, window.innerWidth - 40));
    }
    if (resizeDirection.includes('s')) {
      newHeight = Math.max(400, Math.min(resizeStart.height + deltaY, window.innerHeight - 100));
    }
    if (resizeDirection.includes('n')) {
      newHeight = Math.max(400, Math.min(resizeStart.height - deltaY, window.innerHeight - 100));
    }

    setWindowSize({ width: newWidth, height: newHeight });
  }, [isResizing, resizeDirection, resizeStart]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  // Add/remove resize listeners
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResize);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResize, handleResizeEnd]);

  // Handle drag start
  const handleDragStart = useCallback((e) => {
    if (chatWindowRef.current) {
      const rect = chatWindowRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  }, []);

  // Handle dragging
  const handleDrag = useCallback((e) => {
    if (isDragging && e.clientX && e.clientY) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Constrain to viewport
      const maxX = window.innerWidth - (chatWindowRef.current?.offsetWidth || 400);
      const maxY = window.innerHeight - (chatWindowRef.current?.offsetHeight || 520);
      
      setWindowPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  }, [isDragging, dragOffset]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove drag listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);

  // Reset position and size when closed
  const handleClose = () => {
    setIsOpen(false);
    setIsExpanded(false);
    setWindowPosition({ x: null, y: null });
    setWindowSize({ width: null, height: null });
  };

  // Initial greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = isAuthenticated 
        ? `Hi ${user?.first_name || 'there'}! How can I help you today?`
        : "Hi there! I'm here to answer your questions about CADENCE. What would you like to know?";
      
      setMessages([{
        id: 'greeting',
        role: 'assistant',
        content: greeting,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, messages.length, isAuthenticated, user]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem('cadence_chat_session');
    if (savedSession) {
      setSessionId(savedSession);
    }
  }, []);

  const handleSend = async (presetText) => {
    const text = (typeof presetText === 'string' ? presetText : inputValue).trim();
    if (!text || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    const userInput = text.toLowerCase();
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setSuggestions([]);
    
    // Check if user is responding affirmatively to contact offer
    const affirmativeResponses = ['yes', 'yes please', 'please', 'sure', 'ok', 'okay', 'yeah', 'yep', 'contact me', 'reach out'];
    const lastBotMessage = messages.filter(m => m.role === 'assistant').pop();
    const isRespondingToContactOffer = lastBotMessage && 
      (lastBotMessage.offerLeadCapture ||
       lastBotMessage.content.includes('reach out to you') || 
       lastBotMessage.content.includes('contact us') ||
       lastBotMessage.content.includes('Would you like me to have someone') ||
       lastBotMessage.isCallbackPrompt);
    
    if (affirmativeResponses.some(resp => userInput.includes(resp)) && isRespondingToContactOffer) {
      // Add confirmation message first
      setMessages(prev => [...prev, {
        id: Date.now().toString() + '_confirmation',
        role: 'assistant',
        content: "Perfect! I'll help you get in touch with our team right away.",
        timestamp: new Date().toISOString()
      }]);
      
      // Then show the form
      setTimeout(() => {
        setShowCallbackForm(true);
        setMessages(prev => [...prev, {
          id: Date.now().toString() + '_contact_prompt',
          role: 'assistant',
          content: "Please share your details so our team can reach out to you:",
          timestamp: new Date().toISOString()
        }]);
      }, 500);
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chatbot/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          user_id: user?.id,
          user_name: user ? `${user.first_name} ${user.last_name}` : null,
          user_email: user?.email,
          page_context: window.location.pathname
        })
      });

      const data = await response.json();

      // Save session ID
      if (data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem('cadence_chat_session', data.session_id);
      }

      const botMessage = {
        id: Date.now().toString() + '_bot',
        role: 'assistant',
        content: data.response,
        source: data.source,
        escalated: data.escalated,
        offerLeadCapture: data.offer_lead_capture,
        showQuickQuestions: true,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, botMessage]);
      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);

      // If escalated, offer callback option
      if (data.escalated && !isAuthenticated) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            id: Date.now().toString() + '_callback',
            role: 'assistant',
            content: "Would you like someone from our team to follow up with you personally?",
            isCallbackPrompt: true,
            timestamp: new Date().toISOString()
          }]);
        }, 1000);
      }

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString() + '_error',
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again or contact us directly through the contact page.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Send a suggestion chip directly.
  const askQuestion = (q) => handleSend(q);

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await fetch(`${API_URL}/api/chatbot/request-callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          name: callbackData.name,
          email: callbackData.email,
          question: callbackData.question || messages.filter(m => m.role === 'user').pop()?.content,
          page_context: window.location.pathname
        })
      });

      setMessages(prev => [...prev, {
        id: Date.now().toString() + '_callback_success',
        role: 'assistant',
        content: `Thanks ${callbackData.name.split(' ')[0]}! Someone from CADENCE will reach out to you at ${callbackData.email} soon.`,
        timestamp: new Date().toISOString()
      }]);

      setShowCallbackForm(false);
      setCallbackData({ name: '', email: '', question: '' });

    } catch (error) {
      console.error('Callback request error:', error);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const quickQuestions = [
    "What services do you offer?",
    "How much does it cost?",
    "What states do you serve?",
    "How do I get started?"
  ];

  // Calculate window style for dragging and resizing
  const windowStyle = {
    ...(windowPosition.x !== null ? {
      left: windowPosition.x,
      top: windowPosition.y,
      right: 'auto',
      bottom: 'auto'
    } : {}),
    ...(windowSize.width !== null ? {
      width: windowSize.width,
      height: windowSize.height
    } : {})
  };

  return (
    <>
      {/* Chat Button - Always visible when chat is closed */}
      <button 
        className={`chatbot-button ${isOpen ? 'hidden' : ''} ${position}`}
        onClick={() => setIsOpen(true)}
        data-testid="chatbot-open-btn"
        aria-label="Open chat"
      >
        <MessageCircle size={24} />
        <span className="chatbot-button-label">Ask CADENCE</span>
      </button>

      {/* Chat Window */}
      <div 
        ref={chatWindowRef}
        className={`chatbot-window ${isOpen ? 'open' : ''} ${isExpanded ? 'expanded' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${windowPosition.x !== null || windowSize.width !== null ? 'custom-position' : position}`}
        style={windowStyle}
        data-testid="chatbot-window"
      >
        {/* Resize Handles */}
        <div className="chatbot-resize-handle resize-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
        <div className="chatbot-resize-handle resize-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
        <div className="chatbot-resize-handle resize-se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
        <div className="chatbot-resize-handle resize-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
        <div className="chatbot-resize-handle resize-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />

        {/* Drag Handle */}
        <div 
          className="chatbot-drag-handle"
          onMouseDown={handleDragStart}
          title="Drag to move"
        >
          <GripHorizontal size={16} />
        </div>
        
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-info">
            <div className="chatbot-avatar">
              <img src="/cadence-crest.png" alt="CADENCE" />
            </div>
            <div className="chatbot-header-text">
              <span className="chatbot-title">Ask CADENCE</span>
              <span className="chatbot-status">
                <span className="status-dot"></span>
                Online
              </span>
            </div>
          </div>
          <div className="chatbot-header-actions">
            {/* Expand/Minimize Button */}
            <button 
              className="chatbot-expand"
              onClick={toggleExpand}
              aria-label={isExpanded ? "Minimize chat" : "Expand chat"}
              data-testid="chatbot-expand-btn"
            >
              {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            {/* Close Button */}
            <button 
              className="chatbot-close"
              onClick={handleClose}
              aria-label="Close chat"
              data-testid="chatbot-close-btn"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="chatbot-messages">
          {messages.map((msg) => (
            <div key={msg.id} className={`chatbot-message ${msg.role}`}>
              <div className="message-avatar">
                {msg.role === 'assistant' ? (
                  <Bot size={16} />
                ) : (
                  <User size={16} />
                )}
              </div>
              <div className="message-content">
                <p>{msg.content}</p>
                {msg.isCallbackPrompt && !showCallbackForm && (
                  <button 
                    className="callback-prompt-btn"
                    onClick={() => setShowCallbackForm(true)}
                  >
                    Yes, please contact me
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="chatbot-message assistant">
              <div className="message-avatar">
                <Bot size={16} />
              </div>
              <div className="message-content loading">
                <Loader2 className="spin" size={16} />
                <span>Thinking...</span>
              </div>
            </div>
          )}

          {/* Callback Form */}
          {showCallbackForm && (
            <div className="chatbot-message assistant">
              <div className="message-avatar">
                <Bot size={16} />
              </div>
              <div className="message-content callback-form">
                <form onSubmit={handleCallbackSubmit}>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={callbackData.name}
                    onChange={(e) => setCallbackData({...callbackData, name: e.target.value})}
                    required
                    data-testid="chatbot-callback-name"
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    value={callbackData.email}
                    onChange={(e) => setCallbackData({...callbackData, email: e.target.value})}
                    required
                    data-testid="chatbot-callback-email"
                  />
                  <div className="callback-form-buttons">
                    <button type="submit" className="submit-btn" data-testid="chatbot-callback-submit">Submit</button>
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => setShowCallbackForm(false)}
                      data-testid="chatbot-callback-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Smart suggestions — contextual follow-ups from the AI after each
            reply; the default starter set shows before the first exchange. */}
        {!showCallbackForm && !isLoading && (() => {
          const activeChips = suggestions.length > 0
            ? suggestions
            : (messages.length <= 1 ? quickQuestions : []);
          if (activeChips.length === 0) return null;
          return (
            <div className="chatbot-quick-questions">
              <p>{messages.length <= 1 ? 'Quick questions:' : 'You might also ask:'}</p>
              <div className="quick-question-buttons">
                {activeChips.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => askQuestion(q)}
                    className="quick-question-btn"
                    data-testid={`chatbot-suggestion-${idx}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Input */}
        <div className="chatbot-input-container">
          <input
            type="text"
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            data-testid="chatbot-input"
          />
          <button 
            onClick={() => handleSend()}
            disabled={!inputValue.trim() || isLoading}
            className="send-btn"
            data-testid="chatbot-send-btn"
          >
            <Send size={18} />
          </button>
        </div>

        {/* Footer */}
        <div className="chatbot-footer">
          <span>Powered by CADENCE</span>
        </div>
      </div>
    </>
  );
};

export default Chatbot;
